/**
 * @file        src/hooks/useCompanyInfo.js
 * @description Company details for customer documents/legal pages, with the
 *              postal ADDRESS overlaid live from admin Site Settings so the whole
 *              site prints the one configured address. Falls back to the static
 *              COMPANY defaults while Site Settings has no address yet (the
 *              single-row table can be empty before first config), so nothing
 *              ever renders a blank address. Ported from the neon build (CR A4)
 *              so Beyond's Contact page can show the same admin-editable address
 *              neon already does.
 * @author      Kinetika Labs
 * @created     2026-08-04
 * @changelog
 *   - 2026-09-10 Claude — Overlay the whole ISSUER, not just address/email/phone
 *                          (mirrors neon, unchanged): `name`, `legal`,
 *                          `support`, `web` and `jurisdiction` now come from
 *                          Site Settings too. The invoice and voucher read the
 *                          legal name / email / phone straight off the hardcoded
 *                          lib/company.js, so the customer copy and the admin
 *                          copy of ONE invoice printed different From blocks.
 *   - 2026-08-31 Claude — Overlaid `email`/`phone` too (mirrors the same fix on
 *                          neon). Both were already saved by admin Site
 *                          Settings (`site_settings.email`/`phone`) and already
 *                          returned by GET /api/v1/site-settings — this hook
 *                          just never read them off the response, so the admin
 *                          edit silently did nothing and SiteFooter always
 *                          showed the hardcoded `lib/company.js` values.
 *   - 2026-08-04 Claude — Ported from neon, unchanged.
 *   - 2026-08-05 Claude — Also expose the social_* links (Facebook/Twitter/
 *                          YouTube/Instagram/Google Business) from admin Site
 *                          Settings. Empty string when a platform isn't
 *                          configured — SiteFooter renders an icon only for
 *                          platforms with a non-empty URL here.
 *   - 2026-09-15 Claude — Overlay the STATUTORY identity too: `gstin`, `state`,
 *     `pan`, `registrationNo`, `hsn` and the configurable invoice `terms`
 *     (admin migration 056). The tax invoice printed "GST Number: —" on every
 *     booking because `gstin` had no Site Settings column to read and fell
 *     through to lib/company.js's placeholder — which still carries a "replace
 *     before go-live" TODO. `state` mattered even more: it is the PLACE OF
 *     SUPPLY the invoice compares to decide CGST+SGST vs IGST, and it was a
 *     per-build constant ('Maharashtra'), so a tenant registered elsewhere could
 *     pick the wrong tax head — and disagree with the admin's copy of the same
 *     invoice, which already read the configured state.
 */
import { useQuery } from '@tanstack/react-query';
import { fetchCompanyInfo } from '../api/siteSettings.js';
import { COMPANY as DEFAULTS } from '../lib/company.js';

/**
 * COMPANY details sourced from admin Site Settings (Site Management → Site
 * Setting), falling back to the static build defaults FIELD BY FIELD when a
 * value is empty/unavailable — so nothing renders blank on a tenant that has
 * not filled the form in yet. Every field a document's issuer block prints is
 * overlaid; the remaining defaults (state, gstin) have no Site Settings column
 * to read from.
 */
export function useCompanyInfo() {
  const { data } = useQuery({
    queryKey: ['site-settings', 'company-info'],
    queryFn: fetchCompanyInfo,
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });
  // First non-blank of the named configured values, else '' for the caller to
  // fall back on its build-time default.
  const live = (...keys) => keys.map((k) => String(data?.[k] || '').trim()).find(Boolean) || '';
  const name = live('company_name') || DEFAULTS.name;
  return {
    ...DEFAULTS,
    name,
    // The registered entity the tax invoice bills under. Falls back to the
    // configured TRADING name before the build default — the same order the
    // admin console's useCompany() uses, so the two copies of one invoice name
    // the same company instead of one showing a stale constant.
    legal: live('company_legal_name') || (live('company_name') ? name : DEFAULTS.legal),
    address: live('address') || DEFAULTS.address,
    email: live('email') || DEFAULTS.email,
    phone: live('phone') || DEFAULTS.phone,
    support: live('support_email', 'email') || DEFAULTS.support,
    web: live('website') || DEFAULTS.web,
    jurisdiction: live('jurisdiction', 'city') || DEFAULTS.jurisdiction,
    // Statutory identity printed on the tax invoice's seller block. An invoice
    // without the supplier's GSTIN is not a valid tax invoice.
    gstin: live('gst_no') || DEFAULTS.gstin,
    pan: live('pan') || DEFAULTS.pan,
    registrationNo: live('registration_no') || DEFAULTS.registrationNo,
    hsn: live('hsn_code') || DEFAULTS.hsn,
    // PLACE OF SUPPLY: compared, not printed as a label, so it must be the
    // tenant's configured state rather than a build-time constant.
    state: live('state') || DEFAULTS.state,
    gstStateCode: live('gst_state_code'),
    // Tenant's own invoice clauses, appended after the standard ones.
    terms: live('terms_conditions'),
    // Uploaded brand assets (admin Site Settings). Empty string when not
    // configured — callers fall back to the built-in wordmark / static favicon.
    logoUrl: String(data?.logo_url || '').trim(),
    faviconUrl: String(data?.favicon_url || '').trim(),
    facebookUrl: String(data?.social_facebook || '').trim(),
    twitterUrl: String(data?.social_twitter || '').trim(),
    youtubeUrl: String(data?.social_youtube || '').trim(),
    instagramUrl: String(data?.social_instagram || '').trim(),
    googleUrl: String(data?.social_google || '').trim(),
  };
}
